// ─────────────────────────────────────────────────────────────
// INSIGHTBALL — Design System v2
// Barlow Condensed (display) + Space Grotesk (UI)
// Charte : fond clair, sidebar blanche, accents discrets
// ─────────────────────────────────────────────────────────────

export const FONTS = `@import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:ital,wght@0,400;0,600;0,700;0,800;0,900;1,400&family=Space+Grotesk:wght@300;400;500;600;700&display=swap');`

export const T = {
  // ── Fonds ──────────────────────────────────────────────────
  bg:       '#F7F7F7',   // surface-main
  bgAlt:    '#FAFAFA',   // gray-50
  surface:  '#FFFFFF',   // surface-card
  dark:     '#0A0A0A',   // fond noir (hero, footer, bouton primaire, ticker)
  dark2:    '#111111',   // variante légèrement plus claire

  // ── Sidebar ────────────────────────────────────────────────
  sidebarBg:          '#FFFFFF',
  sidebarBorder:      '#F0F0F0',
  sidebarActiveB:     '#0A0A0A',
  sidebarActiveColor: '#FFFFFF',
  sidebarColor:       '#AAAAAA',
  sidebarSection:     '#D8D8D8',

  // ── Texte ──────────────────────────────────────────────────
  ink:      '#0A0A0A',
  ink2:     '#333333',
  muted:    '#888888',
  muted2:   '#C0C0C0',

  // ── Règles / bordures ──────────────────────────────────────
  rule:     '#EBEBEB',
  ruleDark: '#D8D8D8',

  // ── Bleu accent (anciennement gold) — badges, deltas, accents ──
  gold:     '#4F8EF7',
  goldD:    '#3A7AE0',
  goldBg:   '#EEF4FF',
  goldBg2:  'rgba(79,142,247,0.10)',
  goldBdr:  'rgba(79,142,247,0.25)',

  // ── Accents couleur ────────────────────────────────────────
  green:    '#3DBF7A',
  greenBg:  '#F0FFF6',
  greenBdr: 'rgba(61,191,122,0.25)',

  red:      '#D32F2F',
  redBg:    '#FFF0F0',
  redBdr:   'rgba(211,47,47,0.25)',

  blue:     '#4F8EF7',
  blueBg:   '#EEF4FF',
  blueBdr:  'rgba(79,142,247,0.25)',

  purple:   '#9B6FF7',
  purpleBg: '#F5F0FF',
  purpleBdr:'rgba(155,111,247,0.25)',

  orange:   '#888888',
  orangeBg: '#F5F5F5',
  orangeBdr:'rgba(136,136,136,0.25)',

  // ── Terrain (rapport d'analyse) ────────────────────────────
  // PREMIERE GRANDE SURFACE SOMBRE D'UNE PAGE APPLICATIVE. Le reste de la
  // charte est clair (bg #F7F7F7, ink #0A0A0A) ; `dark` et `dark2` existent
  // mais servent des blocs pleins (hero, footer, ticker), pas un système de
  // surfaces. D'où ce bloc : sur le vert, les couleurs de texte habituelles ne
  // tiennent pas, et il faut leur substituer des équivalents.
  //
  // TOUT CE QUI PORTE LE PRÉFIXE `pitch` EST INVALIDE HORS DU TERRAIN. Le
  // préfixe est là pour que l'erreur se voie à la relecture : `T.pitchAccent`
  // sur un fond blanc est illisible, et le nom le dit.
  //
  // Contrastes relevés sur `pitch` (WCAG 2.1, AA texte normal = 4.5:1) ·
  //   pitchAccent #8FB9FF ......... 5.36:1   ✓
  //   T.gold / T.blue #4F8EF7 ..... 3.33:1   ✗  <- d'où l'existence de pitchAccent
  //   pitchInk #F7F7F7 ............ 9.96:1   ✓
  //   T.green #3DBF7A ............. 4.54:1   ✓ de justesse
  //   T.red #D32F2F ............... 2.14:1   ✗  ne jamais poser sur le terrain
  // Recalculer avant d'ajouter une couleur ici. Le vert de terrain est sombre
  // sans être noir : il pardonne beaucoup moins que `dark`.
  pitch:      '#1C4630',            // fond des tiers
  pitchBand:  '#17402B',            // bandes de tonte alternées (1.09:1 avec pitch, volontaire)
  pitchLine:  'rgba(255,255,255,0.28)',  // marquages sur un terrain de DONNÉES : ils situent, ils ne portent pas
  pitchLineSoft: 'rgba(255,255,255,0.12)', // séparations de tiers, plus discrètes encore
  pitchLineStrong: 'rgba(255,255,255,0.85)', // marquages sur un terrain de TRAVAIL (Insightboard) : ils sont le contenu
  pitchInk:   '#F7F7F7',            // chiffres et libellés sur le terrain
  pitchMuted: 'rgba(247,247,247,0.60)',  // libellés secondaires
  pitchAccent:'#8FB9FF',            // = T.gold remonté en luminosité pour tenir sur pitch
  // TROIS VERTS DE TERRAIN COEXISTAIENT, ils sont réunis ici · #2d6a3a /
  // #27602f (PitchEditor) et #1a4a1a / #1d521d (TacticalPad), tous deux en dur,
  // antérieurs à ce bloc et découverts en le posant. Même intention, trois
  // paires de valeurs. Migrés sur `pitch` / `pitchBand`, changement visuel
  // assumé : une divergence qu'on ne réunit pas au moment où on l'ouvre devient
  // permanente.
  //
  // DEUX POIDS DE LIGNE, ET C'EST VOULU. Le terrain du rapport est un support
  // de données : ses marquages situent les chiffres et doivent s'effacer
  // derrière eux (`pitchLine`). L'Insightboard est un support de travail : ses
  // marquages SONT le contenu, un coach y trace dessus, ils doivent rester
  // francs (`pitchLineStrong`). Ne pas « harmoniser » les deux, ce ne sont pas
  // les mêmes objets.

  // ── Rapport de match (préfixe `rep`) ───────────────────────
  //
  // TOUT CE QUI PORTE LE PRÉFIXE `rep` APPARTIENT AU RAPPORT DE MATCH. Même
  // logique que `pitch*` : le préfixe est là pour que l'erreur se voie à la
  // relecture. `T.repThem` sur un badge de statut ne veut rien dire.
  //
  // DEUX FAMILLES QU'IL NE FAUT PAS CONFONDRE ·
  //   `repUs` / `repThem`   — QUI. Identifient une équipe. Jamais de jugement.
  //   `repUp` / `repDown`   — COMBIEN. Disent un écart, jamais qui a raison.
  //
  // LE ROUGE N'IDENTIFIE JAMAIS UNE ÉQUIPE. Ce fichier portait `HOME_C =
  // '#c0392b'` en dur dans le composant du prestataire : un coach voyait ses
  // propres passes en rouge, couleur qui dit « danger » sur un chiffre qui ne
  // dit rien de tel. `repDown` ne sert qu'à un écart défavorable, et la
  // légende du rapport le déclare au lecteur.
  //
  // UNE SEULE CONVENTION, SUR TOUTE SURFACE QUI AFFICHE UN RAPPORT. Ces tokens
  // ne sont pas réservés à `MatchAnalyseReport`. Ils valent aussi pour
  // `RapportPublic` et `MatchReportShowcase`, qui montrent le même objet à un
  // prospect. C'est précisément parce que la convention n'était écrite que pour
  // le premier que les deux autres l'ont réimplémentée différemment — l'une
  // mettant l'équipe du coach en rouge, l'autre l'adversaire, aucune des deux
  // d'accord avec l'autre. Voir P0-COULEUR-EQUIPE-PUBLIC-001.
  //
  // Contrastes relevés (WCAG 2.1, AA texte normal = 4.5:1, grand texte = 3:1) ·
  //   sur `repHeadBg` #16233A ·
  //     repHeadInk #F7F7F7 ........ 14.68:1  ✓
  //     repUs      #4F8EF7 ......... 4.90:1  ✓  (tient ici, contrairement au
  //                                              terrain vert où il tombe à 3.33)
  //     repThem    #D9A93E ......... 7.26:1  ✓
  //   sur blanc ·
  //     repThem    #D9A93E ......... 2.17:1  ✗  <- d'où repThemInk
  //     repThemInk #91712A ......... 4.57:1  ✓
  //     repUs      #4F8EF7 ......... 3.21:1  ✗  <- d'où repUsInk
  //     repUsInk   #2A66C8 ......... 5.48:1  ✓
  //     repUp      #157F5F ......... 4.96:1  ✓
  //     repDown    #C1462F ......... 5.02:1  ✓
  //   sur leur propre pastille ·
  //     repUp   sur repUpBg ........ 4.20:1  ✗  <- d'où repUpInk 4.55:1
  //     repDown sur repDownBg ...... 4.23:1  ✗  <- d'où repDownInk 4.54:1
  //
  // POURQUOI `repUsInk` EST RELEVÉ SUR TROIS FONDS ET PAS SUR LE SEUL BLANC.
  // Les relevés ci-dessus ne couvraient que le blanc et `repHeadBg`. Les deux
  // pages publiques posaient leurs tableaux de zone sur des gris très clairs
  // (#F5F5F5 et #FAFAFA), où `repThemInk` tombait à 4.19 et 4.37 — sous AA, sans
  // que rien ne le signale, parce que le fond n'avait jamais été relevé.
  //   repUsInk #2A66C8 ·  blanc 5.48  ·  #F5F5F5 5.03  ·  #FAFAFA 5.25   ✓✓✓
  // Le critère retenu est « tient partout », pas « existe déjà » : le candidat
  // #2E6FD4, déjà en production dans MatchReportShowcase, donnait 4.44 sur
  // #F5F5F5 et aurait reconduit le défaut sous un nom propre.
  //
  // Les deux tableaux de zone sont par ailleurs passés en fond BLANC dans le
  // même lot, ce qui rend `repThemInk` suffisant sans toucher au token — donc
  // sans rien changer aux chiffres déjà validés de `MatchAnalyseReport`. Un
  // contraste de zone se fait désormais par une bordure, jamais par un aplat.
  //
  // POURQUOI QUATRE VARIANTES `*Ink`. Les couleurs d'identité ci-dessous sont
  // celles de la maquette et ne bougent pas : elles servent aux aplats, barres
  // et pastilles, où le contraste de TEXTE ne s'applique pas. Dès qu'un
  // libellé ou un chiffre est écrit DANS cette couleur sur fond clair, il faut
  // la variante `*Ink`, assombrie jusqu'à franchir 4.5:1. Même procédé que
  // `pitchAccent`, dans l'autre sens.
  //
  // LA RÈGLE TIENT EN UNE LIGNE ·  aplat → `repUs` / `repThem`
  //                                texte → `repUsInk` / `repThemInk`
  repHeadBg:   '#16233A',   // bandeau d'en-tête, bleu nuit
  repHeadInk:  '#F7F7F7',   // texte du bandeau ......... 14.68:1 sur repHeadBg ✓
  repHeadMuted:'rgba(247,247,247,0.60)',  // libellés secondaires du bandeau

  repUs:       '#4F8EF7',   // NOTRE équipe .............. 4.90:1 sur repHeadBg ✓
                            //          MAIS ............. 3.21:1 sur blanc     ✗
                            // → aplats, barres, pastilles et bandeau UNIQUEMENT.
                            //   Pour du texte sur fond clair : repUsInk.
  repUsInk:    '#2A66C8',   // bleu assombri ............. 5.48:1 sur blanc     ✓
                            //                             5.03:1 sur #F5F5F5   ✓
                            //                             5.25:1 sur #FAFAFA   ✓
                            // NE PAS FUSIONNER avec repUs, ni avec T.blue : le
                            // premier est un aplat, le second est l'accent
                            // d'interface (onglet actif, filet, bordure, CTA).
                            // Trois rôles, trois sorties, même si deux d'entre
                            // elles portent aujourd'hui la même valeur.
  repThem:     '#D9A93E',   // l'ADVERSAIRE .............. 7.26:1 sur repHeadBg ✓
                            //          MAIS ............. 2.17:1 sur blanc     ✗
                            // → aplats, barres et bandeau UNIQUEMENT.
                            //   Pour du texte sur fond clair : repThemInk.
  repThemInk:  '#91712A',   // ambre assombri ............ 4.57:1 sur blanc     ✓
                            // NE PAS FUSIONNER avec repThem : un aplat et un
                            // texte n'ont pas les mêmes contraintes. Même
                            // raison d'être que pitchAccent, dans l'autre sens.
                            // Suffisant sur BLANC seulement · 4.19 sur #F5F5F5
                            // et 4.37 sur #FAFAFA. D'où le passage des deux
                            // tableaux de zone en fond blanc, plutôt que
                            // d'assombrir ce token déjà validé en production.

  // LE ROUGE RESTE ICI, ET SEULEMENT ICI · `repDown` dit un écart défavorable.
  // Une couleur ne peut pas dire à la fois QUI et COMMENT.
  repUp:       '#157F5F',   // écart FAVORABLE ........... 4.96:1 sur blanc     ✓
  repUpBg:     '#E0F0E8',   //          repUp dessus ..... 4.20:1               ✗
  repUpInk:    '#14795A',   // → texte sur repUpBg ....... 4.55:1               ✓
  repDown:     '#C1462F',   // écart DÉFAVORABLE ......... 5.02:1 sur blanc     ✓
  repDownBg:   '#FAE8E3',   //          repDown dessus ... 4.23:1               ✗
  repDownInk:  '#B9432D',   // → texte sur repDownBg ..... 4.54:1               ✓
                            // Les quatre `*Ink` existent parce que la couleur
                            // d'identité ne passe pas AA en texte. Les retirer
                            // « pour simplifier » remet le défaut en place.

  // ── Terrain du rapport (préfixe `repPitch`) ────────────────
  //
  // PAS DE VERT GAZON. Le terrain du rapport n'illustre pas une pelouse, il
  // porte des chiffres : le fond reste neutre et c'est l'INTENSITÉ du bleu qui
  // dit la possession. Les lignes suffisent à le rendre identifiable.
  //
  // À ne pas confondre avec le bloc `pitch*` plus haut, qui est un VRAI terrain
  // vert — support de dessin de l'Insightboard et de PitchEditor. Deux objets
  // différents : l'un se regarde, l'autre se lit.
  //
  // L'intensité passe par `fillOpacity` sur `repUs`, jamais par une couleur
  // fabriquée. Elle est PLAFONNÉE à 0.24 : au-delà, le texte posé dessus perd
  // son contraste.
  //
  // Contrastes relevés sur le pire fond, un tiers rempli à 0.24 (= #D1E0F9) ·
  //   T.ink        #0A0A0A ......... 14.84:1  ✓
  //   repThemInk   #91712A ..........  3.42:1  ✗  <- ne tient PAS ici
  //   repPitchThem #7A5F23 ..........  4.51:1  ✓  (6.02:1 sur blanc)
  // D'où un ambre propre au terrain, plus sombre que `repThemInk` qui suffit
  // sur fond blanc. Ne pas les fusionner : ils ne se posent pas sur le même fond.
  repPitchBg:    '#FAFAFA',   // = T.bgAlt, fond neutre du terrain
  repPitchLine:  '#D8D8D8',   // = T.ruleDark, marquages
  repPitchThem:  '#7A5F23',   // valeur adverse, lisible jusqu'à fillOpacity .24

  // ── Bandeau de la page match (préfixe `mh`, match header) ──
  //
  // Même logique de préfixe que `pitch*` et `rep*` : hors du bandeau de
  // `MatchDetail`, ces tokens ne veulent rien dire.
  //
  // Le fond `mhBg` REPREND EXACTEMENT la valeur qui était en dur dans
  // `MatchDetail.jsx` (`D = '#1a1916'`), et `mhInk` celle de `CREAM`. Aucun
  // changement visuel n'est introduit ici : on nomme ce qui existait, on ne le
  // redessine pas. Le reste du fichier porte encore onze couleurs en dur —
  // chantier séparé, voir le ticket P2-COULEURS-EN-DUR-MATCHDETAIL.
  //
  // Contrastes relevés sur `mhBg` #1A1916 (WCAG 2.1, AA texte = 4.5:1) ·
  //   mhInk   #F5F2EB ......................... 15.72:1  ✓
  //   mhUs    #4F8EF7 (= T.blue) ...............  5.48:1  ✓
  //   mhMuted rgba(245,242,235,.55) → #92908B ..  5.51:1  ✓
  // L'opacité de `mhMuted` ne descend pas sous .55 : à .40 elle tombe à
  // 3.55:1 et ne passe plus AA pour du texte.
  mhBg:    '#1A1916',
  mhInk:   '#F5F2EB',
  mhMuted: 'rgba(245,242,235,0.55)',
  mhRule:  'rgba(245,242,235,0.14)',   // filets internes, séparation seule
  mhUs:    '#4F8EF7',                  // le score du club, = T.blue

  // ── Stat card accents (border-top) ─────────────────────────
  accentStat1: '#0A0A0A',
  accentStat2: '#4F8EF7',
  accentStat3: '#9B6FF7',
  accentStat4: '#3DBF7A',

  // ── Typo ───────────────────────────────────────────────────
  display: "'Barlow Condensed', sans-serif",
  mono:    "'Space Grotesk', sans-serif",
  body:    "'Space Grotesk', sans-serif",

  // ── Layout ─────────────────────────────────────────────────
  sidebarW: 220,
  radius:   3,
}

// ── Helpers ────────────────────────────────────────────────────
export const statusConfig = {
  completed:  { label: 'Terminé',    color: T.green,  bg: T.greenBg,  bdr: T.greenBdr  },
  processing: { label: 'En cours',   color: T.blue,   bg: T.blueBg,   bdr: T.blueBdr   },
  pending:    { label: 'En attente', color: T.orange, bg: T.orangeBg, bdr: T.orangeBdr },
  error:      { label: 'Erreur',     color: T.red,    bg: T.redBg,    bdr: T.redBdr    },
}


// ── IMPRESSION ────────────────────────────────────────────────────────────
//
// `@media print` PLUTOT QU'UN PARAMETRE D'URL. Le meme chemin sert le Ctrl+P
// du coach et le `page.pdf()` du backend : pas de code conditionnel, pas de
// seconde route, pas de rendu qui pourrait diverger de l'ecran.
//
// POURQUOI `!important` PARTOUT · toute la mise en forme de l'application est
// en styles INLINE (`style={{…}}`), qui l'emportent sur une feuille externe.
// Sans `!important`, aucune de ces regles ne mordrait. Ce n'est pas une
// facilite, c'est la seule facon de surcharger de l'inline.
//
// LE CIBLAGE PASSE PAR `data-print`, pose explicitement sur les elements
// concernes. Pas de selecteur de structure (`aside`, `nav`, `:nth-child`) :
// il casserait au premier remaniement de mise en page, en silence, et
// personne ne s'en apercevrait avant qu'un coach imprime.
export const printStyles = `
@media print {
  /* Papier · A4 portrait, marges suffisantes pour le pied de page repete */
  @page { size: A4 portrait; margin: 12mm 10mm 16mm; }

  html, body {
    background: #fff !important;
    width: 100% !important;
    overflow: visible !important;

    /* ── AUCUNE LIGATURE SUR PAPIER · PDF-RAPPORT-001 ────────────────────
       Deux rapports partis chez un coach affichaient « on aaque vers la
       droite ». Les deux t avaient disparu.

       CE N'EST PAS UNE POLICE MANQUANTE. Chromium remplace « tt » par un
       SEUL glyphe de ligature, et comme Space Grotesk est servie par Google
       Fonts en police VARIABLE, il ne peut pas embarquer son contour : il
       ecrit une police de Type 3, ou chaque glyphe est une procedure de
       dessin qu'il fabrique lui-meme. Verifie sur le PDF produit · le « tt »
       sort en <</ActualText (tt)>> sur une ressource de police distincte.
       Quand cette procedure part vide dans le conteneur Render, le glyphe ne
       dessine rien et le mot perd ses deux lettres d'un coup.

       LA GARDE DES POLICES NE POUVAIT PAS L'ATTRAPER · elle verifie les NOMS
       de police embarques, pas que les glyphes dessinent quelque chose.

       PORTEE REELLE, MESUREE et non supposee · tt, ff, fi, ffi, fl, ffl
       forment toutes une ligature. Le rapport en est plein — « attaques »,
       « nettement », « offensif », « efficacite », « chiffres », « affiche »
       — et les donnees du coach aussi : un club « Cottet », un joueur
       « Attia » cassent pareil. Corriger le mot visible n'aurait rien regle.

       ON COUPE DONC LA SUBSTITUTION, pas le symptome. Le rendu ecran n'est
       pas touche : il lit la vraie police et n'a jamais eu le defaut.
       La propriete s'herite, la poser ici couvre tout le document. */
    font-variant-ligatures: none !important;
  }

  /* ── RIEN D'INTERACTIF NE S'IMPRIME · REGLE INVERSEE ──────────────────
     Un bouton sur papier est du bruit : on ne peut pas cliquer dessus.

     La premiere version masquait au cas par cas, par data-print="hide".
     Elle a laisse passer « Supprimer ce match » dans un PDF destine a un
     coach · 39 boutons dans MatchDetail, 4 marques. Marquer les 35 restants
     n'aurait rien regle — le prochain bouton ajoute serait a nouveau oublie.
     (Pas de backtick dans ce bloc : il est lui-meme dans un template literal.)

     La regle est donc inversee · TOUT element interactif disparait, et ce
     qui doit rester s'annonce par data-print="keep". Un oubli fait
     desormais disparaitre quelque chose, ce qui se voit, au lieu d'imprimer
     une action destructrice, ce qui ne se voit pas. */
  button, input, select, textarea,
  [role="button"], [data-print="hide"] { display: none !important; }
  [data-print="keep"] { display: revert !important; }

  /* Le contenu reprend toute la largeur une fois la barre laterale retiree */
  [data-print="main"] {
    margin-left: 0 !important;
    padding: 0 !important;
    min-height: 0 !important;
    background: #fff !important;
  }

  /* VARIANTE CLAIRE · les aplats sombres deviennent blancs bordes.
     Le bleu charte, l'ambre, le vert et le rouge RESTENT : ce sont des textes
     et des aplats fins, ils portent l'information et coutent peu d'encre. */
  [data-print="light"] {
    background: #fff !important;
    border: 1px solid ${'#D8D8D8'} !important;
    color: ${'#0A0A0A'} !important;
  }
  [data-print="light"] * { color: ${'#0A0A0A'} !important; }
  [data-print="light"] [data-print="keep-color"],
  [data-print="light"] [data-print="keep-color"] * { color: revert !important; }

  /* Un bloc ne se coupe jamais en deux · ni un terrain, ni un tableau */
  [data-print="block"] { break-inside: avoid !important; page-break-inside: avoid !important; }
  [data-print="page-break"] { break-before: page !important; page-break-before: always !important; }
  svg, table, tr { break-inside: avoid !important; page-break-inside: avoid !important; }

  /* Pied de page repete sur CHAQUE page · Chromium repete un element en
     position fixed a l'impression, c'est le seul moyen sans en-tetes natifs. */
  [data-print="footer"] {
    display: block !important;
    position: fixed !important;
    bottom: 0 !important; left: 0 !important; right: 0 !important;
    font-family: 'Space Grotesk', sans-serif !important;
    font-size: 8pt !important;
    color: #666 !important;
    border-top: 0.5px solid #D8D8D8 !important;
    padding-top: 3mm !important;
  }

  /* Les liens ne sont pas cliquables sur papier : pas de soulignement bleu */
  a { text-decoration: none !important; color: inherit !important; }
}
`

export const globalStyles = `
  ${FONTS}
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html, body { overflow-x: hidden; width: 100%; }
  body { font-family: 'Space Grotesk', sans-serif; background: #F7F7F7; color: #0A0A0A; }
  h1, h2, h3 { font-family: 'Barlow Condensed', sans-serif; }
  ::-webkit-scrollbar { width: 4px; height: 4px; background: transparent; }
  ::-webkit-scrollbar-thumb { background: #EBEBEB; }
  select, input { -webkit-appearance: none; }
  select option { background: #ffffff; color: #0A0A0A; }
  @keyframes fadeUp   { from { opacity:0; transform:translateY(14px); } to { opacity:1; transform:translateY(0); } }
  @keyframes fadeIn   { from { opacity:0; } to { opacity:1; } }
  @keyframes spin     { to { transform:rotate(360deg); } }
  @keyframes pulse    { 0%,100%{opacity:1} 50%{opacity:.4} }
  @keyframes shimmer  { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
  ${printStyles}
`
